import { useParams, useNavigate } from 'react-router-dom';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useEventById } from '@/hooks/useEvents';
import { MapPin, Clock, Tag, ChevronLeft, Home } from 'lucide-react';
import Colors from '@/constants/colors';
import PageContainer from '@/components/PageContainer';
import ContentSection from '@/components/ContentSection';
import { LexicalRenderer } from '@/components/LexicalRenderer';
import { DetailActionButtons } from '@/components/DetailActionButtons';
import { ScrollingBackButton } from '@/components/ScrollingBackButton';
import { useNavigationSource } from '@/hooks/useNavigationSource';

const categoryLabels: Record<string, Record<string, string>> = {
  community: { en: 'Community', ru: 'Сообщество', pl: 'Społeczność' },
  cultural: { en: 'Cultural', ru: 'Культурные', pl: 'Kulturalne' },
  legal: { en: 'Legal', ru: 'Правовые', pl: 'Prawne' },
  social: { en: 'Social', ru: 'Социальные', pl: 'Społeczne' },
};

export default function EventDetail() {
  const { language } = useAppLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isDirectLink } = useNavigationSource();

  const { data: event, isLoading, error } = useEventById(id || '', language);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ru' ? 'ru-RU' : language === 'pl' ? 'pl-PL' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(language === 'ru' ? 'ru-RU' : language === 'pl' ? 'pl-PL' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateRange = (startDate: string, endDate?: string) => {
    if (!endDate) {
      return `${formatDate(startDate)} • ${formatTime(startDate)}`;
    }

    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);
    const sameDay = startDateObj.toDateString() === endDateObj.toDateString();

    if (sameDay) {
      return `${formatDate(startDate)} • ${formatTime(startDate)} - ${formatTime(endDate)}`;
    }

    return `${formatDate(startDate)} • ${formatTime(startDate)} - ${formatDate(endDate)} • ${formatTime(endDate)}`;
  };

  if (isLoading) {
    return (
      <PageContainer>
        <ContentSection style={{ padding: '24px', textAlign: 'center' }}>
          <p style={styles.notFound}>
            {language === 'ru' ? 'Загрузка события...' : language === 'pl' ? 'Ładowanie zdarzenia...' : 'Loading event...'}
          </p>
        </ContentSection>
      </PageContainer>
    );
  }

  if (error || !event) {
    return (
      <PageContainer>
        <ContentSection style={{ padding: '24px', textAlign: 'center' }}>
          <p style={styles.notFound}>
            {language === 'ru' ? 'Событие не найдено' : language === 'pl' ? 'Zdarzenie nie znalezione' : 'Event not found'}
          </p>
        </ContentSection>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {event.imageUrl && <ScrollingBackButton />}

      {/* Hero Section */}
      {event.imageUrl && (
        <div style={styles.heroContainer}>
          <img
            src={event.imageUrl}
            alt={event.title}
            style={styles.heroImage}
            loading="lazy"
            decoding="async"
          />
          <div style={styles.heroOverlay} />
          <div style={styles.heroContent}>
            <div style={styles.heroTopRow}>
              <button
                style={styles.backButton}
                onClick={() => isDirectLink ? navigate('/') : navigate(-1)}
                aria-label={isDirectLink ? 'Go to home' : 'Go back'}
              >
                {isDirectLink ? <Home size={24} color="white" /> : <ChevronLeft size={24} color="white" />}
              </button>
              <DetailActionButtons
                id={event.id}
                title={event.title}
                shareUrl={`/events/${event.id}`}
                hideShare={isDirectLink}
              />
            </div>
            <h1 style={styles.heroTitle}>{event.title}</h1>
          </div>
        </div>
      )}

      {/* Header row when no hero image */}
      {!event.imageUrl && (
        <ContentSection style={styles.noHeroHeader}>
          <button
            style={styles.noHeroBackButton}
            onClick={() => isDirectLink ? navigate('/') : navigate(-1)}
            aria-label={isDirectLink ? 'Go to home' : 'Go back'}
          >
            {isDirectLink ? <Home size={24} color={Colors.text} /> : <ChevronLeft size={24} color={Colors.text} />}
          </button>
          <DetailActionButtons
            id={event.id}
            title={event.title}
            shareUrl={`/events/${event.id}`}
            variant="light"
            hideShare={isDirectLink}
          />
        </ContentSection>
      )}

      {/* Category Badge */}
      <ContentSection style={styles.badgeContainer}>
        <span style={styles.badge}>
          {categoryLabels[event.category]?.[language as keyof typeof categoryLabels[string]] || event.category}
        </span>
        {event.featured && <span style={styles.featuredBadge}>★ Featured</span>}
      </ContentSection>

      {/* Title (shown only if no hero image) */}
      {!event.imageUrl && (
        <ContentSection style={{ padding: '0 24px 16px' }}>
          <h1 style={styles.title}>{event.title}</h1>
        </ContentSection>
      )}

      {/* Meta Information */}
      <ContentSection style={styles.metaSection}>
        {/* Date & Time */}
        <div style={styles.metaItem}>
          <Clock size={20} color={Colors.primary} style={{ flexShrink: 0 }} />
          <div style={styles.metaContent}>
            <span style={styles.metaLabel}>
              {language === 'ru' ? 'Дата и время' : language === 'pl' ? 'Data i godzina' : 'Date & Time'}
            </span>
            <span style={styles.metaValue}>{formatDateRange(event.startDate, event.endDate)}</span>
          </div>
        </div>

        {/* Location */}
        {event.location && (
          <div style={styles.metaItem}>
            <MapPin size={20} color={Colors.primary} style={{ flexShrink: 0 }} />
            <div style={styles.metaContent}>
              <span style={styles.metaLabel}>
                {language === 'ru' ? 'Место' : language === 'pl' ? 'Lokalizacja' : 'Location'}
              </span>
              <span style={styles.metaValue}>{event.location}</span>
            </div>
          </div>
        )}

        {/* Category */}
        <div style={styles.metaItem}>
          <Tag size={20} color={Colors.primary} style={{ flexShrink: 0 }} />
          <div style={styles.metaContent}>
            <span style={styles.metaLabel}>
              {language === 'ru' ? 'Категория' : language === 'pl' ? 'Kategoria' : 'Category'}
            </span>
            <span style={styles.metaValue}>
              {categoryLabels[event.category]?.[language as keyof typeof categoryLabels[string]] || event.category}
            </span>
          </div>
        </div>
      </ContentSection>

      {/* Description */}
      <ContentSection style={styles.descriptionSection}>
        <h2 style={styles.sectionTitle}>
          {language === 'ru' ? 'О событии' : language === 'pl' ? 'O zdarzeniu' : 'About Event'}
        </h2>
        <LexicalRenderer content={event.description || ''} />
      </ContentSection>

      {/* Spacing for bottom nav */}
      <div style={{ height: '40px' }} />
    </PageContainer>
  );
}

const styles = {
  heroContainer: {
    position: 'relative',
    width: '100%',
    height: '300px',
    overflow: 'hidden',
  } as React.CSSProperties,

  heroImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  } as React.CSSProperties,

  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6))',
  } as React.CSSProperties,

  heroContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '16px 24px',
  } as React.CSSProperties,

  heroTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  } as React.CSSProperties,

  backButton: {
    background: 'rgba(0,0,0,0.4)',
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s ease',
  } as React.CSSProperties,

  heroTitle: {
    margin: 0,
    fontSize: '32px',
    fontWeight: '700',
    color: 'white',
    lineHeight: '1.2',
  } as React.CSSProperties,

  noHeroHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
  } as React.CSSProperties,

  noHeroBackButton: {
    background: Colors.ice,
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s ease',
  } as React.CSSProperties,

  badgeContainer: {
    padding: '24px 24px 0',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  badge: {
    display: 'inline-block',
    padding: '6px 14px',
    backgroundColor: Colors.primaryLight,
    color: Colors.primary,
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  } as React.CSSProperties,

  featuredBadge: {
    display: 'inline-block',
    padding: '6px 14px',
    backgroundColor: `${Colors.accent}20`,
    color: Colors.accent,
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  } as React.CSSProperties,

  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: Colors.text,
    margin: 0,
    padding: '24px 24px 16px',
    lineHeight: '1.3',
  } as React.CSSProperties,

  metaSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    padding: '16px 24px',
  } as React.CSSProperties,

  metaItem: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
  } as React.CSSProperties,

  metaContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    flex: 1,
  } as React.CSSProperties,

  metaLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: Colors.textSecondary,
    textTransform: 'uppercase' as const,
  } as React.CSSProperties,

  metaValue: {
    fontSize: '15px',
    fontWeight: '500',
    color: Colors.text,
    lineHeight: '1.4',
  } as React.CSSProperties,

  descriptionSection: {
    padding: '24px',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: Colors.text,
    margin: '0 0 16px 0',
  } as React.CSSProperties,

  bodyText: {
    fontSize: '15px',
    lineHeight: '1.6',
    color: Colors.text,
    margin: 0,
  } as React.CSSProperties,

  notFound: {
    color: Colors.textSecondary,
  } as React.CSSProperties,
};
